import type {CodeRecord,Specialty} from '../data/types';
import {medicalSpecialties} from '../data/specialties';

export const specialties: {id:Specialty;label:string;short:string}[] = [
  {id:'all',label:'Tutte le specializzazioni',short:'Tutte'},
  ...[...medicalSpecialties].sort((a,b)=>a.label.localeCompare(b.label,'it'))
];

export const SPECIALTY_PRIORS_VERSION = '2026.10.11-1';
type PrefixPrior = {prefixes:readonly string[];weight:number};
type SectionPrior = {from:number;to:number;weight:number};
interface SpecialtyPrior {
  diagnosis?:readonly PrefixPrior[];
  procedure?:readonly PrefixPrior[];
  sections?:readonly SectionPrior[];
  procedureBaseline?:number;
}
const prefixes=(weight:number,...prefixes:string[]):PrefixPrior=>({prefixes,weight});
const sections=(from:number,to=from,weight=1):SectionPrior=>({from,to,weight});
const neutral:SpecialtyPrior={};

// Editorial chapter/section priors, not official specialty assignments. Every
// prefix is checked against the pinned catalog. Transversal specialties stay
// neutral rather than inheriting internal medicine or inferring patient age.
// The seven existing profiles keep their original weights and stable IDs.
export const specialtyPriors:Record<Exclude<Specialty,'all'>,SpecialtyPrior> = {
  orthopedics:{diagnosis:[prefixes(1,'M','S'),prefixes(.5,'T')],sections:[sections(76,84)]},
  general_surgery:{diagnosis:[prefixes(1,'K'),prefixes(.3,'T')],sections:[sections(42,54)]},
  internal_medicine:{diagnosis:[prefixes(.7,'A','B','D','E','I','J','K')],procedureBaseline:.1},
  cardiology:{diagnosis:[prefixes(1,'I')],sections:[sections(35,39)]},
  urology:{diagnosis:[prefixes(1,'N')],sections:[sections(55,64)]},
  gynecology:{diagnosis:[prefixes(1,'O'),prefixes(.3,'N')],sections:[sections(65,75)]},
  neurology:{diagnosis:[prefixes(1,'G')],sections:[sections(0,5)]},
  allergology:{diagnosis:[prefixes(1,'D8','J30','J45','J46','L20','L23','L50'),prefixes(.5,'T78')]},
  anatomical_pathology:{sections:[sections(90,91,.5)],procedure:[prefixes(1,'89.8')]},
  anesthesiology:{diagnosis:[prefixes(1,'R52','R57','J96')],procedure:[prefixes(1,'03.9','04.8','96.7','96.8'),prefixes(.5,'93.9','96.0')]},
  audiology:{diagnosis:[prefixes(1,'H6','H7','H8','H9','R47','R49')],procedure:[prefixes(1,'95.4','93.7')]},
  cardiac_surgery:{diagnosis:[prefixes(1,'I')],sections:[sections(35,37)]},
  child_neuropsychiatry:{diagnosis:[prefixes(.7,'F','G')],sections:[sections(94)]},
  clinical_pathology:{sections:[sections(90,91)]},
  clinical_pharmacology:{diagnosis:[prefixes(1,'T36','T37','T38','T39','T4','T5','T60','T61','T62','T63','T64','T65')]},
  community_medicine:neutral,
  dermatology:{diagnosis:[prefixes(1,'L')],sections:[sections(86)]},
  emergency_medicine:neutral,
  endocrinology:{diagnosis:[prefixes(1,'E')],sections:[sections(6,7,.5)]},
  forensic_medicine:neutral,
  gastroenterology:{diagnosis:[prefixes(1,'K')],sections:[sections(42,54)]},
  geriatrics:neutral,
  health_statistics:neutral,
  infectious_diseases:{diagnosis:[prefixes(1,'A','B','U07')]},
  maxillofacial_surgery:{diagnosis:[prefixes(1,'K0','S02')],sections:[sections(23,27),sections(76)]},
  medical_genetics:{diagnosis:[prefixes(.7,'Q')]},
  medical_oncology:{diagnosis:[prefixes(1,'C'),prefixes(.5,'D0')],procedure:[prefixes(1,'99.25','99.28')]},
  microbiology:{diagnosis:[prefixes(.5,'A','B')],sections:[sections(90,91,.5)]},
  nephrology:{diagnosis:[prefixes(1,'N0','N1','N2')],sections:[sections(55,56,.5)],procedure:[prefixes(1,'54.98')]},
  neurosurgery:{diagnosis:[prefixes(1,'G','S06')],sections:[sections(1,5)]},
  nuclear_medicine:{procedure:[prefixes(1,'92.0','92.1')]},
  nutrition:{diagnosis:[prefixes(1,'E4','E5','E6')],procedure:[prefixes(1,'96.6')]},
  occupational_medicine:neutral,
  ophthalmology:{diagnosis:[prefixes(1,'H0','H1','H2','H3','H4','H5')],sections:[sections(8,16)],procedure:[prefixes(1,'95.0','95.1','95.2','95.3')]},
  otolaryngology:{diagnosis:[prefixes(1,'H6','H7','H8','H9','J3')],sections:[sections(18,22),sections(25,31)]},
  palliative_medicine:neutral,
  pediatric_surgery:{diagnosis:[prefixes(.7,'P','Q')],sections:[sections(42,54,.5)]},
  pediatrics:{diagnosis:[prefixes(.7,'P','Q')]},
  plastic_surgery:{diagnosis:[prefixes(.7,'L'),prefixes(.3,'S','T')],sections:[sections(85,86)]},
  psychiatry:{diagnosis:[prefixes(1,'F')],sections:[sections(94)]},
  public_health:neutral,
  pulmonology:{diagnosis:[prefixes(1,'J')],sections:[sections(32,34)],procedure:[prefixes(1,'93.9','96.7','96.8')]},
  radiology:{sections:[sections(87,88)]},
  radiotherapy:{diagnosis:[prefixes(.5,'C','D0')],procedure:[prefixes(1,'92.2','92.3','92.4')]},
  rehabilitation:{diagnosis:[prefixes(.5,'M','G')],sections:[sections(93)]},
  rheumatology:{diagnosis:[prefixes(1,'M')]},
  sports_medicine:{diagnosis:[prefixes(.5,'M','S')],sections:[sections(93,93,.5)]},
  thermal_medicine:neutral,
  thoracic_surgery:{diagnosis:[prefixes(1,'J')],sections:[sections(32,34)]},
  vascular_surgery:{diagnosis:[prefixes(1,'I7','I8')],sections:[sections(38,39)]},
  hematology:{diagnosis:[prefixes(1,'D5','D6','D7','D8','C8','C9','D45','D46','D47')],sections:[sections(41)],procedure:[prefixes(1,'99.0')]}
};

export function hasSpecialtyPrior(s:Specialty) {
  return s!=='all'&&Object.keys(specialtyPriors[s]||{}).length>0;
}

export function specialtyWeight(r:CodeRecord,s:Specialty):number {
  if(s==='all')return 0;
  const profile=specialtyPriors[s];
  if(!profile)return 0;
  const rules=r.kind==='diagnosis'?profile.diagnosis:profile.procedure;
  let weight=r.kind==='procedure'?(profile.procedureBaseline||0):0;
  for(const rule of rules||[])if(rule.prefixes.some(prefix=>r.code.startsWith(prefix)))weight=Math.max(weight,rule.weight);
  if(r.kind==='procedure') {
    const section=Number(r.code.slice(0,2));
    for(const rule of profile.sections||[])if(section>=rule.from&&section<=rule.to)weight=Math.max(weight,rule.weight);
  }
  return weight;
}
